import Point, { point } from '../Point';
import SVG from 'svg.js';

export type PointArr = [number, number];
export type LineArr = [number, number, number, number];
export type LineOfPoints = [PointArr, PointArr];
export type Line = LineOfPoints | LineArr;

export const reflect = ([x1, y1]: number[], [x2, y2]: number[]): [number, number] => [x2 - x1 + x2, y2 - y1 + y2];

export const isSame = (x1: number, y1: number, x2: number, y2: number) => x1 === x2 && y1 === y2;

export const getPointFromEvent = (
  event: PointerEvent,
  toolSpace: { node: SVGElement }
): { clientX: number; clientY: number } => {
  const { clientX, clientY } = event;

  const { x, y } = point.call(toolSpace, clientX, clientY);

  return {
    clientX: x,
    clientY: y,
  };
};

export const getClosestPoint = (p1: [number, number], candidates: [number, number][], maxDistance = Infinity) => {
  let closest: [number, number] | undefined;
  let closestDist = Infinity;
  candidates.forEach((candidate) => {
    const dist = Math.hypot(candidate[0] - p1[0], candidate[1] - p1[1]);
    if (dist < closestDist) {
      closest = candidate;
      closestDist = dist;
    }
  });

  return closestDist < maxDistance ? closest! : p1;
};

export type Candidate = {
  p: PointArr;
  lines: LineArr[];
};

export const indexOfClosestCandidate = (p1: [number, number], candidates: Candidate[], maxDistance = Infinity) => {
  let closestDist = Infinity;
  let closestIndex = -1;
  candidates.forEach((candidate, index) => {
    const dist = Math.hypot(candidate.p[0] - p1[0], candidate.p[1] - p1[1]);
    if (dist < closestDist) {
      closestDist = dist;
      closestIndex = index;
    }
  });

  return closestDist < maxDistance ? closestIndex : -1;
};

export const distance = (p1: PointArr, p2: PointArr) => {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  return Math.hypot(x2 - x1, y2 - y1);
};

export const distanceMany = (p1: PointArr, p2: PointArr[]) => {
  return Math.min(
    ...p2.map((p) => {
      return distance(p1, p);
    })
  );
};

export const snapToPointAtAngle = (
  p1: [number, number],
  p2: [number, number],
  snapEnabled: boolean,
  radians: number | number[] = Math.PI / 4,
  maxDist = Infinity
) => {
  if (!snapEnabled) return p2;

  const vec = [p2[0] - p1[0], p2[1] - p1[1]];
  const len = Math.sqrt(Math.pow(vec[0], 2) + Math.pow(vec[1], 2));

  // calculate all possible vectors at multiple of the passed radians from 0 to 360
  if (!Array.isArray(radians)) {
    const angle = Math.atan2(0, 1);
    const rad = radians;
    radians = Array.from({ length: Math.floor((2 * Math.PI) / rad) }, (_, index) => index * rad + angle);
  }

  const candidates = radians.map((angle) => {
    const vecAtAngle = [Math.cos(angle), Math.sin(angle)];
    const x = p1[0] + vecAtAngle[0] * len;
    const y = p1[1] + vecAtAngle[1] * len;
    return [x, y] satisfies [number, number];
  });

  return getClosestPoint(p2, candidates, maxDist);
};

export const snapToPointOnLine = (p: PointArr, line: Line): PointArr => {
  const [x0, y0] = p;
  const [x1, y1, x2, y2] = line.flat();

  const dx = x2 - x1;
  const dy = y2 - y1;
  const det = dx * dx + dy * dy;
  const t = (dy * (y0 - y1) + dx * (x0 - x1)) / det;

  return [x1 + t * dx, y1 + t * dy];
};

/**
 * Snaps a line to a given point.
 * @param line - The line to snap, defined by a start and end point.
 * @param snapPoint - The point to which the line should be snapped.
 * @returns A new line snapped to the given point.
 */
export const snapLineToPoint = (line: Line, snapPoint: PointArr): LineArr => {
  const [x1, y1, x2, y2] = line.flat();
  const [px, py] = snapPoint;

  // Calculate the vector of the line
  const dx = x2 - x1;
  const dy = y2 - y1;

  // Create a new line that is snapped to the point
  const snappedLine: Line = [px, py, px + dx, py + dy];

  return snappedLine;
};

/**
 * Calculates the distance from a point to a line segment.
 * @param line - The line segment defined by its start and end points.
 * @param point - The point to calculate the distance to.
 * @returns The shortest distance from the point to the line segment.
 */
export const distanceFromPointToLine = (line: Line, point: PointArr): number => {
  const [x1, y1, x2, y2] = line.flat();
  const [px, py] = point;

  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const len_sq = C * C + D * D;
  let t = -1;
  if (len_sq !== 0) t = dot / len_sq;

  // if (param < 0) {
  //   xx = x1;
  //   yy = y1;
  // } else if (param > 1) {
  //   xx = x2;
  //   yy = y2;
  // } else {
  const xx = x1 + t * C;
  const yy = y1 + t * D;
  // }

  const dx = px - xx;
  const dy = py - yy;
  return Math.sqrt(dx * dx + dy * dy);
};

export const equalCPRight = (segment: [number, number, number, number, number, number]) => {
  return segment[2] === segment[4] && segment[3] === segment[5];
};

export const euqalCPLeft = (segment: [number, number, number, number, number, number]) => {
  return segment[0] === segment[2] && segment[1] === segment[3];
};

export const snapToPointsOfInterest = (
  p1: [number, number],
  p2: [number, number],
  path: [number, number, number, number, number, number][],
  state: {
    interestLine?: [[number, number], [number, number]];
  },
  maxDist = 50
) => {
  const lineSegments = path.reduce(
    (acc, curr, index) => {
      if (index < path.length - 1) {
        if (equalCPRight(curr) && euqalCPLeft(path[index + 1])) {
          const p1 = curr.slice(2, 4) as [number, number];
          const p2 = path[index + 1].slice(0, 2) as [number, number];
          acc.push([p1, p2]);
        }
      }
      return acc;
    },
    [] as [[number, number], [number, number]][]
  );

  if (!lineSegments.length) return p2;

  const candidates = lineSegments.map((line) => snapToPointOnLine(p2, line));

  const closest = getClosestPoint(p2, candidates, maxDist);

  const index = candidates.indexOf(closest);

  state.interestLine = lineSegments[index];

  return closest;
};

export const intersectLineAndRay = (line: Line, ray: Line): [number, number] | null => {
  const [x1, y1, x2, y2] = line.flat();
  const [x3, y3, x4, y4] = ray.flat();

  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (denominator === 0) {
    return null;
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;

  if (t >= 0 && t <= 1) {
    const intersectionX = x1 + t * (x2 - x1);
    const intersectionY = y1 + t * (y2 - y1);
    return [intersectionX, intersectionY];
  }

  return null;
};

export const intersect = (line1: Line, line2: Line): PointArr | null => {
  const [x1, y1, x2, y2] = line1.flat();
  const [x3, y3, x4, y4] = line2.flat();

  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (denominator === 0) {
    return null;
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;

  const intersectionX = x1 + t * (x2 - x1);
  const intersectionY = y1 + t * (y2 - y1);
  return [intersectionX, intersectionY];
};

export const toPathToolPath = (arr: number[][]) => {
  arr = arr.slice(1, -1);

  let [leftX, leftY, posX, posY] = arr[arr.length - 1].slice(3);

  const obj: [leftX: number, leftY: number, x: number, y: number, rightX: number, rightY: number][] = [];

  arr.forEach((segment) => {
    obj.push([leftX, leftY, posX, posY, segment[1], segment[2]]);

    [leftX, leftY, posX, posY] = segment.slice(3);
  });

  return obj;
};

const calculateSlope = (p1: PointArr, p2: PointArr): number => {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  return (y2 - y1) / (x2 - x1);
};

const rotateSlope = (slope: number, angle: number): number => {
  const angleRad = angle * (Math.PI / 180); // convert angle to radians
  const tanAngle = Math.tan(angleRad);
  return (slope + tanAngle) / (1 - slope * tanAngle);
};

const getPointFromSlope = (point: PointArr, slope: number, distance: number = 1): PointArr => {
  const [x, y] = point;
  const newX = x + distance;
  const newY = y + slope * distance;
  return [newX, newY];
};

export const calculate45DegSnapLines = (p1: PointArr, p2: PointArr): LineArr[] => {
  const originalSlope = calculateSlope(p1, p2);

  const angles = [45, 90, 135];
  const generateLines = (point: PointArr) =>
    angles.map((angle) => {
      const slope = rotateSlope(originalSlope, angle);
      return [...point, ...getPointFromSlope(point, slope)] as LineArr;
    });

  return [...generateLines(p1), ...generateLines(p2)];
};
